import { ExaminationDisplay } from '@modules/examination/models/examination-display';
import { ExaminationDisplaysByExaminationFormId } from '@modules/examination/models/examination-displays-by-examination-form-id';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { StoreUser } from '../users/models/store-user';
import { selectUsers } from '../users/users.selectors';
import { storeExaminationToExaminationResponse } from './adapters/store-examination.adapter';
import { ExaminationState } from './examinations.state';
import { StoreExamination } from './models/store-examination';

export const EXAMINATIONS_FEATURE_KEY = 'Examinations';

export const selectExaminationsState = createFeatureSelector<ExaminationState>(EXAMINATIONS_FEATURE_KEY);

const storeExaminationToExaminationDisplay = (
  examinations: StoreExamination[],
  users: { [userId: number]: StoreUser },
): ExaminationDisplay[] =>
  examinations.map((examination) =>
    storeExaminationToExaminationResponse(examination, users[examination.createdBy], users[examination.updatedBy]),
  );

const indexExaminationList = (examinations: ExaminationDisplay[]): ExaminationDisplaysByExaminationFormId => {
  const examinationsByFormId = {} as ExaminationDisplaysByExaminationFormId;

  examinations.forEach((examination) => {
    (examinationsByFormId[examination.examinationFormId] ??= []).push(examination);
  });

  return examinationsByFormId;
};

export const selectExaminations = ({
  clientCode,
  patientId,
  departmentId,
}: {
  clientCode: string;
  patientId: number;
  departmentId?: number;
}): MemoizedSelector<ExaminationState, ExaminationDisplaysByExaminationFormId> =>
  createSelector(selectExaminationsState, selectUsers, (state, users) => {
    const patientExaminationState = state?.[clientCode]?.[patientId];

    if (!patientExaminationState) {
      return {};
    }

    if (departmentId) {
      const examinationIds = patientExaminationState.departments?.[departmentId] || [];

      const examinations = examinationIds
        .map((examinationId) => patientExaminationState.examinations[examinationId])
        .filter((examination): examination is StoreExamination => !!examination);

      return indexExaminationList(storeExaminationToExaminationDisplay(examinations, users));
    }

    return indexExaminationList(
      storeExaminationToExaminationDisplay(Object.values(patientExaminationState?.examinations || {}), users),
    );
  });
